import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type OperatorCloseRequest, type OperatorCloseStatus } from "../api/client";

/**
 * Fermeture opérateur « Fermer cette jambe » (2026-11-02).
 *
 * <p>Polle {@code GET /api/cycles/operator-close} (5 s) tant que {@code enabled} (cycle actif), et
 * resserre à 1 s pendant qu'une demande est PENDING/CONSUMED pour afficher l'issue (FILLED / REFUSED)
 * dès le tick suivant. {@code request(dealId)} pose la demande ; les 409 remontent en
 * {@link OperatorCloseError} (code + motif humanisable).
 */

export class OperatorCloseError extends Error {
  readonly code: string;
  readonly reason: string;
  constructor(code: string, reason: string) {
    super(`${code}: ${reason}`);
    this.code = code;
    this.reason = reason;
  }
}

export interface OperatorCloseState {
  status: OperatorCloseStatus | null;
  pending: boolean;
  request: (dealId: string) => Promise<OperatorCloseRequest>;
  cancel: () => Promise<void>;
  refresh: () => Promise<void>;
}

const IDLE_POLL_MS = 5_000;
const BUSY_POLL_MS = 1_000;

export function useOperatorClose(enabled: boolean): OperatorCloseState {
  const [status, setStatus] = useState<OperatorCloseStatus | null>(null);
  const [pending, setPending] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setStatus(await api.operatorCloseStatus());
    } catch {
      // Silencieux — la bannière d'état système porte l'injoignabilité backend.
    }
  }, []);

  const busy = status?.request?.status === "PENDING" || status?.request?.status === "CONSUMED";

  useEffect(() => {
    if (!enabled) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await api.operatorCloseStatus();
        if (!cancelled) setStatus(s);
      } catch {
        // idem
      }
    };
    void tick();
    const id = setInterval(tick, busy ? BUSY_POLL_MS : IDLE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, busy]);

  const request = useCallback(
    async (dealId: string): Promise<OperatorCloseRequest> => {
      setPending(true);
      try {
        const r = await api.requestOperatorClose(dealId);
        await refresh();
        return r.request;
      } catch (e) {
        if (e instanceof ApiError && typeof e.body === "object" && e.body !== null) {
          const b = e.body as { error?: string; reason?: string };
          throw new OperatorCloseError(b.error ?? "UNKNOWN", b.reason ?? "Refus sans motif.");
        }
        throw e;
      } finally {
        setPending(false);
      }
    },
    [refresh],
  );

  const cancel = useCallback(async () => {
    setPending(true);
    try {
      await api.cancelOperatorClose();
      await refresh();
    } finally {
      setPending(false);
    }
  }, [refresh]);

  return { status, pending, request, cancel, refresh };
}
